import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { IBlock, IHighLight, IPost, IFollowingEntry, IIndividualProfile, ILeaderProfile } from '../services/interfaces';
import { baseTheme } from '../theme';
import { getPostDetails, getUserProfileData } from '../services';
import IndividualCreateForm from '../components/account-forms/create-account-forms/individual-create-form';
import { useDispatch, useSelector } from 'react-redux';
import { followLeaderGroup, resetLeadersPage } from '../store';
import { getMyLeaders } from '../store/leaders/actions';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        padding: theme.spacing(3),
      },
      heading: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
      group: {
        fontWeight: 'bold',
      },
      postTitle: {
        fontSize: '1.4em',
        padding: theme.spacing(0),
        margin: theme.spacing(0),
      },
      postDate: {
        color: theme.palette.text.primary,
      },
      content: {
        color: theme.palette.text.primary,
        paddingTop: theme.spacing(1),
      },
      contentBody: {
        border: 'solid 1px #cdcdcd',
        paddingBottom: theme.spacing(1),
        minHeight: '200px',
        maxHeight: '500px',
        overflowY: 'scroll',
      },
      highLightBodySpacer: {
        paddingTop: theme.spacing(3),
      },
      highLight: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        borderBottom: `solid 1px ${theme.palette.text.disabled}`,
      },
      previewImage: {
        width: '100px',
        paddingRight: theme.spacing(1),
      },
      button: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.paper,
        borderRadius: '5px',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        width: '150px',
        '&:hover': {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.dark,
        },
      },
    }),
);

const basePostData: IPost = {
  title: '',
  group: {
    id: '',
    name: '',
    type: 'Private',
  },
  date: '',
  owner: '',
  admins: [],
  creation: '',
  updated: '',
  content: '',
  linkUrl: '',
  tags: [],
  posted: false,
  public: false,
  searchTerms: [],
  archived: false,
};

export default function SharedPost(props: any) {
  const [post, setPost] = useState<IPost>(basePostData);
  const classes = useStyles(baseTheme);
  const postId = props ? props.match.params.id : null;
  useEffect(() => {
    if (props.match.params.id) {
      getPostDetails(props.match.params.id).then((post: IPost) => {
        retrieveAndStoreGroupLeaderData(post.owner || '');
        setPost(post);
      });
    }
  }, [props.match.params.id]);
  const scrollToForm = () => {
    const signUoForm = document.getElementById('sign-up-form');
    if (signUoForm) {
      signUoForm.scrollIntoView();
    }
  };
  const renderHighlightImage = (image: string | undefined) => {
    return image ? (<img
            src={image}
            alt="preview"
            className={classes.previewImage}
        />) : <div className={classes.previewImage}>&nbsp;</div>;
  };
  const sorter = (a: any, b: any) => {
    if (a.order < b.order) {
      return -1;
    }

    if (a.order > b.order) {
      return 1;
    }

    return 0;
  };
  const renderHighlights = (highlights: IHighLight[]) =>
      highlights ?
          highlights.sort(sorter).map((highlight: IHighLight) => (
            <Grid container item xs={12} key={highlight.id} className={classes.highLight}>
                <Grid>
                    {renderHighlightImage(highlight.image)}
                </Grid>
                <Grid>
                  {highlight.content}
                </Grid>
            </Grid>
          ))
      : null;
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.auth);
  const currUser = useSelector((state: any) => state.auth.currUser);
  const profile = useSelector((state: any) => state.user.profile);
  const following = useSelector((state: any) => state.leaders.follow);
  const [initPageLoad, setInitPageLoad] = useState(true);
  const [groupLeaderProfile, setGroupLeaderProfile] =
      useState<IIndividualProfile | ILeaderProfile | undefined>(undefined);

  useEffect(() => {
    if (initPageLoad && currUser) {
      dispatch(resetLeadersPage());
      getMyLeaders(currUser.uid, dispatch);
      setInitPageLoad(false);
    }
  }, [initPageLoad, currUser, post.owner, dispatch]);
  const retrieveAndStoreGroupLeaderData = async (ownerId: string) => {
    if (ownerId) {
      const groupLeaderData = await getUserProfileData(ownerId);
      setGroupLeaderProfile(groupLeaderData);
    }
  };
  const userIsAlreadyFollowing = () : boolean => {
    const groupId = post && post.group && post.group.id ? post.group.id : null;
    let userIsFollowing = false;
    following.forEach((followingEntry: IFollowingEntry) => {
      if (groupId && followingEntry?.groups?.length > 0) {
        followingEntry.groups.forEach((group: IGroupType) => {
          if (group.id === groupId) {
            userIsFollowing = true;
          }
        });
      }
    });
    return userIsFollowing;
  };
  interface IGroupType {
    id: string;
  }
  const generateFollowData = () => {
    const groups: IGroupType[] = [];
    let followData = {
      groups,
      leader: {
        id: '',
        name: '',
        displayName: '',
      },
    };
    following.forEach((followingEntry: IFollowingEntry) => {
      if (post.owner && followingEntry?.leader?.id === post.owner) {
        followData = followingEntry;
      }
    });
    if (!followData.leader.id) {
      followData.leader.id = post.owner || '';
      followData.leader.displayName = groupLeaderProfile?.displayName || '';
    }
    const groupId = post && post.group && post.group.id ? post.group.id : null;
    if (!groupId) {
      return null;
    }
    followData.groups.push({
      id: groupId,
    });
    return followData;
  };
  const generateMemberData = (status: string) => ({
    status,
    type: 'follower',
    uid: currUser.uid,
    displayName: currUser.displayName,
    email: currUser.email,
  });
  const generateFollowingGroup = () => ({
    leader: {
      displayName: groupLeaderProfile?.displayName,
      id: post.owner,
    },
    groups: [],
  });
  const handleAddPublicGroup = (event: any) => {
    event.stopPropagation();
    const groupId = post && post.group && post.group.id ? post.group.id : '';
    followLeaderGroup(
        false,
        generateMemberData('approved'),
        groupId,
        generateFollowData(),
        dispatch,
    ).then(() => setInitPageLoad(true));
  };
  const highlights = post && post.highlights && post.highlights.length > 0 ? post.highlights : [];
  const groupName = post.group && post.group.name ? post.group.name : 'Post Name Placeholder';
  const userIsAlreadyFollowingGroup = userIsAlreadyFollowing();
  const postTitle = post.title ? post.title : 'Post Title Placeholder';
  const postDate = post.date ? post.date : 'Post Date Placeholder';
  let postContent =  'Post Content Placeholder';
  if (post.content) {
    postContent = '';
    const contentJson = JSON.parse(post.content);
    contentJson.blocks.forEach((block: IBlock) => {
      postContent += `${block.text}\n`;
    });
  }
  return (
    <Grid className={classes.root}>
      {post &&
        <Grid>
          <Grid item container className={classes.heading}>
            <Grid
              item
              container
              direction="column"
              xs={11}
            >
              <Grid item className={classes.group}>
                {groupName}
              </Grid>
              {!(auth.authenticated && profile) &&
                <h4>
                    Login or Sign Up to Follow
                </h4>
              }
              {userIsAlreadyFollowingGroup &&
                <h4>
                    You are already following this group.
                </h4>
              }
              {(auth.authenticated && profile) && !userIsAlreadyFollowingGroup &&
                  <Button className={classes.button} onClick={handleAddPublicGroup}>
                    Follow Group
                  </Button>
              }
              <Grid item>
                <h4 className={classes.postTitle}>
                  {postTitle}
                </h4>
              </Grid>
                <Grid item className={classes.postDate}>
                  {postDate}
                </Grid>
              </Grid>
          </Grid>
          <Grid item className={classes.content}>
            <b>Content:</b>
            <Grid item className={classes.contentBody}>
              {postContent}
            </Grid>
            <div className={classes.highLightBodySpacer}></div>
              <b>Highlights:</b>
                {renderHighlights(highlights)}
          </Grid>
        </Grid>}
        {!(auth.authenticated && profile) &&
        <Grid container justify="center" alignItems="center">
            <Grid item md={12} lg={4}>
                <div id="sign-up-form">
                    <IndividualCreateForm submitText="Sign up FREE" themeStyle="light"/>
                </div>
            </Grid>
        </Grid>}
    </Grid>
  );
}
